'use client';

/**
 * Globale Suche (Command-Palette) fuer das ProHandel-Portal.
 *
 * Oeffnet ueber die Lupe im demo1-Header bzw. Strg/Cmd+K (nur ProHandel-Kontext).
 * Drei Gruppen, je max. 5 Treffer:
 *   1. Menuepunkte  — clientseitig aus dem geladenen Menue (MenuProvider), sofort
 *   2. Adressen     — GET /api/prohandel/adressen?suchbegriff=…  (Typeahead-Modus:
 *                     mit_anzahl=false, mit_saldi=false → ~150 ms)
 *   3. Artikel      — GET /api/prohandel/artikel?search=…
 * Server-Suchen: Debounce 250 ms, min. 2 Zeichen, parallel; jede neue Eingabe
 * bricht die laufenden Requests per AbortController ab (keine veralteten Treffer).
 * Tastatur: ↑/↓ (cmdk), Enter = oeffnen, Esc = schliessen (Dialog).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Command as CommandPrimitive } from 'cmdk';
import {
  AlertCircle,
  Building2,
  ChevronRight,
  CornerDownLeft,
  LayoutGrid,
  Loader2,
  Package,
  Search,
  SearchX,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { BackendAuth } from '@/lib/auth-backend';
import { useTranslation } from '@/hooks/useTranslation';
import { useMenu } from '@/providers/menu-provider';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { buildMenuIndex, searchMenu } from './menu-index';

const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;
const PER_GROUP = 5;

const EMPTY_GROUP = { items: [], loading: false, error: null };

function useDebounced(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const isAbort = (err, signal) => signal?.aborted || err?.name === 'AbortError';

/* ------------------------------------------------------------------ */
/* Ergebnis-Zeilen                                                     */
/* ------------------------------------------------------------------ */

function ResultItem({ value, icon: Icon, title, subtitle, meta, onSelect }) {
  return (
    <CommandPrimitive.Item
      value={value}
      onSelect={onSelect}
      className={cn(
        'group flex cursor-pointer select-none items-center gap-3 rounded-md px-2.5 py-2 text-sm text-foreground outline-hidden',
        'data-[selected=true]:bg-primary/10',
      )}
    >
      <span className="flex size-8 shrink-0 items-center justify-center rounded-md border border-border bg-muted/50 text-muted-foreground group-data-[selected=true]:border-primary/30 group-data-[selected=true]:text-primary">
        <Icon className="size-4" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{title}</span>
        {subtitle ? (
          <span className="block truncate text-xs text-muted-foreground">{subtitle}</span>
        ) : null}
      </span>
      {meta ? <span className="shrink-0 text-xs text-muted-foreground">{meta}</span> : null}
      <CornerDownLeft
        className="size-3.5 shrink-0 text-muted-foreground opacity-0 group-data-[selected=true]:opacity-100"
        aria-hidden="true"
      />
    </CommandPrimitive.Item>
  );
}

function GroupState({ loading, error, label }) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 px-2.5 py-2 text-xs text-muted-foreground" role="status">
        <Loader2 className="size-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
        {label}
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex items-start gap-2 px-2.5 py-2 text-xs text-destructive" role="alert">
        <AlertCircle className="mt-px size-3.5 shrink-0" aria-hidden="true" />
        <span className="line-clamp-2">{error}</span>
      </div>
    );
  }
  return null;
}

const groupClass =
  'px-1.5 py-1 [&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-muted-foreground';

function Kbd({ children }) {
  return (
    <kbd className="inline-flex h-5 min-w-5 items-center justify-center rounded border border-border bg-muted px-1 font-sans text-[10px] font-medium text-muted-foreground">
      {children}
    </kbd>
  );
}

/* ------------------------------------------------------------------ */
/* Dialog                                                              */
/* ------------------------------------------------------------------ */

export function GlobalSearchDialog({ open, onOpenChange }) {
  const router = useRouter();
  const { t } = useTranslation();
  const tx = useCallback(
    (key, de, opts = {}) => t(`global_search.${key}`, { defaultValue: de, editable: false, ...opts }),
    [t],
  );

  const { headerMenu, menuData } = useMenu();
  const menuIndex = useMemo(() => buildMenuIndex(headerMenu, menuData), [headerMenu, menuData]);

  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState('');
  const [adressen, setAdressen] = useState(EMPTY_GROUP);
  const [artikel, setArtikel] = useState(EMPTY_GROUP);
  const controllerRef = useRef(null);

  const trimmed = query.trim();
  const debounced = useDebounced(trimmed, DEBOUNCE_MS);
  const active = trimmed.length >= MIN_CHARS;
  // Zwischen Tippen und Debounce gelten die Server-Gruppen schon als "laedt",
  // damit kein "Keine Treffer" aufblitzt.
  const pending = active && debounced !== trimmed;

  // Menue: sofort, ohne Debounce (rein clientseitig).
  const menuHits = useMemo(
    () => (active ? searchMenu(menuIndex, trimmed, PER_GROUP) : []),
    [active, menuIndex, trimmed],
  );

  // Server-Suchen (parallel, abbrechbar).
  useEffect(() => {
    controllerRef.current?.abort();
    if (!open || debounced.length < MIN_CHARS) {
      setAdressen(EMPTY_GROUP);
      setArtikel(EMPTY_GROUP);
      return undefined;
    }
    const controller = new AbortController();
    controllerRef.current = controller;
    const { signal } = controller;
    const q = encodeURIComponent(debounced);

    setAdressen((s) => ({ ...s, loading: true, error: null }));
    setArtikel((s) => ({ ...s, loading: true, error: null }));

    BackendAuth.apiCallJson(
      `/api/prohandel/adressen?suchbegriff=${q}&page_size=${PER_GROUP}&mit_anzahl=false&mit_saldi=false`,
      { signal },
    )
      .then((res) => {
        if (signal.aborted) return;
        setAdressen({ items: (res?.items || []).slice(0, PER_GROUP), loading: false, error: null });
      })
      .catch((err) => {
        if (isAbort(err, signal)) return;
        setAdressen({ items: [], loading: false, error: err?.message || 'Fehler' });
      });

    BackendAuth.apiCallJson(`/api/prohandel/artikel?search=${q}&page_size=${PER_GROUP}`, { signal })
      .then((res) => {
        if (signal.aborted) return;
        setArtikel({ items: (res?.artikel || []).slice(0, PER_GROUP), loading: false, error: null });
      })
      .catch((err) => {
        if (isAbort(err, signal)) return;
        setArtikel({ items: [], loading: false, error: err?.message || 'Fehler' });
      });

    return () => controller.abort();
  }, [debounced, open]);

  // Beim Schliessen alles zuruecksetzen (naechstes Oeffnen startet leer).
  useEffect(() => {
    if (!open) {
      controllerRef.current?.abort();
      setQuery('');
      setSelected('');
    }
  }, [open]);

  const adrLoading = active && (pending || adressen.loading);
  const artLoading = active && (pending || artikel.loading);
  const adrItems = useMemo(() => (active ? adressen.items : []), [active, adressen.items]);
  const artItems = useMemo(() => (active ? artikel.items : []), [active, artikel.items]);

  // Auswahl stabil halten: verschwindet der markierte Treffer, springt sie auf den ersten.
  const keys = useMemo(
    () => [
      ...menuHits.map((m) => m.key),
      ...adrItems.map((a) => `adr-${a.adr000}`),
      ...artItems.map((a) => `art-${a.ark000}`),
    ],
    [menuHits, adrItems, artItems],
  );
  useEffect(() => {
    if (keys.length && !keys.includes(selected)) setSelected(keys[0]);
  }, [keys, selected]);

  const go = useCallback(
    (href, target) => {
      onOpenChange(false);
      if (target === '_blank') {
        window.open(href, '_blank', 'noopener');
        return;
      }
      router.push(href);
    },
    [onOpenChange, router],
  );

  const nothingFound =
    active && !adrLoading && !artLoading && keys.length === 0 && !adressen.error && !artikel.error;

  const sep = ' › ';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        close={false}
        className="top-[12vh] max-w-[640px] translate-y-0 gap-0 overflow-hidden p-0 sm:top-[15vh]"
        aria-describedby="global-search-desc"
      >
        <DialogTitle className="sr-only">{tx('title', 'Suche')}</DialogTitle>
        <DialogDescription id="global-search-desc" className="sr-only">
          {tx('description', 'Menüpunkte, Adressen und Artikel durchsuchen')}
        </DialogDescription>

        <CommandPrimitive
          shouldFilter={false}
          loop
          value={selected}
          onValueChange={setSelected}
          className="flex w-full flex-col bg-background text-foreground"
          label={tx('title', 'Suche')}
        >
          <div className="flex items-center gap-2 border-b border-border px-4">
            <Search className="size-4.5 shrink-0 text-muted-foreground" aria-hidden="true" />
            <CommandPrimitive.Input
              autoFocus
              value={query}
              onValueChange={setQuery}
              placeholder={tx('placeholder', 'Menüpunkt, Adresse oder Artikel suchen …')}
              className="h-13 w-full bg-transparent py-3 text-base text-foreground outline-hidden placeholder:text-muted-foreground"
            />
            {(adrLoading || artLoading) && (
              <Loader2
                className="size-4 shrink-0 animate-spin text-muted-foreground motion-reduce:animate-none"
                aria-label={tx('loading', 'Suche läuft …')}
              />
            )}
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="shrink-0 rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground hover:text-foreground"
              aria-label={tx('close', 'Schließen')}
            >
              Esc
            </button>
          </div>

          <CommandPrimitive.List className="max-h-[min(60vh,480px)] overflow-y-auto overscroll-contain py-1">
            {!active && (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                <Search className="mx-auto mb-2 size-6 opacity-40" aria-hidden="true" />
                {tx('hint', 'Mindestens 2 Zeichen eingeben – durchsucht Menüpunkte, Adressen und Artikel.')}
              </div>
            )}

            {active && menuHits.length > 0 && (
              <CommandPrimitive.Group heading={tx('group_menu', 'Menüpunkte')} className={groupClass}>
                {menuHits.map((m) => (
                  <ResultItem
                    key={m.key}
                    value={m.key}
                    icon={LayoutGrid}
                    title={m.title}
                    subtitle={m.breadcrumb.length ? [...m.breadcrumb, m.title].join(sep) : null}
                    onSelect={() => go(m.path, m.target)}
                  />
                ))}
              </CommandPrimitive.Group>
            )}

            {active && (adrItems.length > 0 || adrLoading || adressen.error) && (
              <CommandPrimitive.Group heading={tx('group_addresses', 'Adressen')} className={groupClass}>
                {adrItems.map((a) => {
                  const name = [a.adr030, a.adr040].filter((s) => s && String(s).trim()).join(' ');
                  const ort = [a.adr080 || '', a.adr090 || ''].join(' ').trim();
                  const role = [
                    a.adr220 ? tx('customer', 'Kunde') : null,
                    a.adr230 ? tx('supplier', 'Lieferant') : null,
                  ]
                    .filter(Boolean)
                    .join(' / ');
                  return (
                    <ResultItem
                      key={`adr-${a.adr000}`}
                      value={`adr-${a.adr000}`}
                      icon={Building2}
                      title={name || a.adr010 || `#${a.adr000}`}
                      subtitle={[
                        a.adr005 ? `${tx('account_short', 'Kto.')} ${a.adr005}` : null,
                        ort || null,
                        role || null,
                        a.adr999 ? tx('blocked', 'gesperrt') : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                      onSelect={() => go(`/prohandel/stammdaten/adressen/${a.adr000}`)}
                    />
                  );
                })}
                {adrItems.length === 0 && (
                  <GroupState
                    loading={adrLoading}
                    error={adressen.error}
                    label={tx('loading_addresses', 'Adressen werden gesucht …')}
                  />
                )}
              </CommandPrimitive.Group>
            )}

            {active && (artItems.length > 0 || artLoading || artikel.error) && (
              <CommandPrimitive.Group heading={tx('group_articles', 'Artikel')} className={groupClass}>
                {artItems.map((a) => (
                  <ResultItem
                    key={`art-${a.ark000}`}
                    value={`art-${a.ark000}`}
                    icon={Package}
                    title={a.name || a.artikel_nummer || `#${a.ark000}`}
                    subtitle={[
                      a.artikel_nummer ? `${tx('article_no_short', 'Art.-Nr.')} ${a.artikel_nummer}` : null,
                      a.name_lang && String(a.name_lang).trim() ? a.name_lang : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                    meta={
                      a.lagerbestand !== undefined && a.lagerbestand !== null
                        ? `${tx('stock_short', 'Lager')} ${Number(a.lagerbestand).toLocaleString('de-AT')}`
                        : null
                    }
                    onSelect={() =>
                      go(`/prohandel/stammdaten/artikel?id=${encodeURIComponent(a.ark000)}`)
                    }
                  />
                ))}
                {artItems.length === 0 && (
                  <GroupState
                    loading={artLoading}
                    error={artikel.error}
                    label={tx('loading_articles', 'Artikel werden gesucht …')}
                  />
                )}
              </CommandPrimitive.Group>
            )}

            {nothingFound && (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                <SearchX className="mx-auto mb-2 size-6 opacity-40" aria-hidden="true" />
                {tx('empty', 'Keine Treffer für „{{query}}“', { query: trimmed })}
              </div>
            )}
          </CommandPrimitive.List>

          <div className="hidden items-center gap-4 border-t border-border bg-muted/40 px-4 py-2 text-[11px] text-muted-foreground sm:flex">
            <span className="inline-flex items-center gap-1">
              <Kbd>↑</Kbd>
              <Kbd>↓</Kbd>
              {tx('kbd_navigate', 'auswählen')}
            </span>
            <span className="inline-flex items-center gap-1">
              <Kbd>
                <CornerDownLeft className="size-3" aria-hidden="true" />
              </Kbd>
              {tx('kbd_open', 'öffnen')}
            </span>
            <span className="inline-flex items-center gap-1">
              <Kbd>Esc</Kbd>
              {tx('kbd_close', 'schließen')}
            </span>
            <span className="ms-auto inline-flex items-center gap-1">
              <ChevronRight className="size-3" aria-hidden="true" />
              <Kbd>Strg</Kbd>
              <Kbd>K</Kbd>
            </span>
          </div>
        </CommandPrimitive>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Strg/Cmd+K oeffnet/schliesst die Suche — nur wenn `enabled` (ProHandel-Kontext).
 */
export function useGlobalSearchHotkey(enabled, setOpen) {
  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, setOpen]);
}

export default GlobalSearchDialog;
